import React, { useMemo } from "react";
import Icon from "../../../components/AppIcon";

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v || 0)
  );

const PayableAgingChart = ({ aging, topSuppliers }) => {
  const buckets = useMemo(() => {
    const b = aging?.buckets || {
      due0_30: { amount: 0, count: 0 },
      due31_60: { amount: 0, count: 0 },
      due61_90: { amount: 0, count: 0 },
      due90p: { amount: 0, count: 0 },
    };
    const items = [
      { key: "due0_30", label: "Atual (0-30)", color: "bg-success" },
      { key: "due31_60", label: "31-60 dias", color: "bg-warning" },
      { key: "due61_90", label: "61-90 dias", color: "bg-orange-500" },
      { key: "due90p", label: "+90 dias", color: "bg-error" },
    ];
    const max = Math.max(
      1,
      ...items.map((i) => Number(b?.[i.key]?.amount || 0))
    );

    return items.map((i) => {
      const amount = Number(b?.[i.key]?.amount || 0);
      const count = Number(b?.[i.key]?.count || 0);
      const pct = (amount / max) * 100;
      return { ...i, amount, count, pct };
    });
  }, [aging]);

  const total = Number(aging?.totalOpen || 0);

  const risk = useMemo(() => {
    const due61 = Number(aging?.buckets?.due61_90?.amount || 0);
    const due90 = Number(aging?.buckets?.due90p?.amount || 0);
    const over60 = due61 + due90;
    const pct = total > 0 ? (over60 / total) * 100 : 0;
    const low = Math.max(0, 100 - pct);
    const med = pct * 0.6;
    const high = pct * 0.4;
    return {
      over60,
      pct,
      low: Math.min(100, low),
      med: Math.min(100, med),
      high: Math.min(100, high),
    };
  }, [aging, total]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-card rounded-lg border border-border card-shadow lg:col-span-2">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-heading-semibold text-lg text-card-foreground mb-1">
              Análise de Aging - Contas a Pagar
            </h3>
            <p className="text-sm text-muted-foreground">
              Distribuição das obrigações por período de vencimento
            </p>
          </div>

          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total a pagar</p>
            <p className="font-heading-semibold text-lg text-card-foreground">
              {fmtBRL(total)}
            </p>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div className="h-48 flex items-end justify-between gap-4 border-b border-border pb-4">
              {buckets.map((b) => (
                <div key={b.key} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className={`w-full rounded-t-lg ${b.color}`}
                      style={{ height: `${Math.max(2, b.pct)}%` }}
                      title={`${b.label}: ${fmtBRL(b.amount)}`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{b.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {buckets.map((b) => {
                const pct = total > 0 ? (b.amount / total) * 100 : 0;
                return (
                  <div
                    key={`${b.key}-card`}
                    className="p-3 rounded-lg border border-border bg-accent/20"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${b.color}`} />
                      <span className="text-xs text-muted-foreground">{b.label}</span>
                    </div>
                    <p className="font-heading-semibold text-sm text-card-foreground">
                      {fmtBRL(b.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {b.count} título(s) • {pct.toFixed(1)}%
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="p-4 rounded-lg border border-border bg-accent/20">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div>
                  <p className="font-body-medium text-sm text-card-foreground">
                    Análise de Risco de Liquidez
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {risk.pct.toFixed(1)}% do total ({fmtBRL(risk.over60)}) vence em mais de 60 dias
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Baixo Risco: {risk.low.toFixed(0)}%</span>
                    <span>Médio Risco: {risk.med.toFixed(0)}%</span>
                    <span>Alto Risco: {risk.high.toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {total === 0 && (
              <div className="text-sm text-muted-foreground">
                Sem dados para exibir (verifique período/base/status).
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border card-shadow">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading-semibold text-lg text-card-foreground mb-1">
            Top Fornecedores
          </h3>
          <p className="text-sm text-muted-foreground">Maiores valores em aberto</p>
        </div>

        <div className="p-6 space-y-4">
          {(topSuppliers || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados para exibir</p>
          ) : (
            topSuppliers.slice(0, 5).map((s) => (
              <div
                key={s.name}
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-accent/20"
              >
                <div>
                  <p className="font-body-medium text-sm text-card-foreground">
                    {s.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Venc: {s.nextDueBR || "-"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading-semibold text-sm text-card-foreground">
                    {fmtBRL(s.amount)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.statusLabel}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PayableAgingChart;
